import { useCallback, useMemo, useRef, useState, type PointerEvent } from 'react';
import { Panel } from '../../components/Panel.tsx';
import { duration, NO_VALUE } from '../../components/format.ts';
import { Figure, unread, unreadLine, type Read } from '../../components/states/index.ts';
import { FeedTag } from './FeedTag.tsx';
import type { Health } from './freshness.ts';
import { axisClock, frac, ticks } from '../../components/timeScale.ts';
import { CMD_TONE, IN_FLIGHT, latencyMs, percentile } from './commandModel.ts';
import type { LedgerCommand } from './useCommandLedger.ts';
import styles from './FlowStrip.module.css';

/* ==========================================================================
   DISPATCH FLOW — the board's time axis.

   Every other pane on this surface answers "what is the queue like *now*".
   This one is the spine THE BAR §5 asks for: commands as marks at their true
   timestamps, on one shared x-scale, with the quantity an operator actually
   judges the platform by — how long a command took to settle — on the y.

   Two lanes, one scale:

     IN FLIGHT   a tick per unsettled command at the instant it was issued.
                 Unsettled commands have no latency yet, so they are NOT
                 plotted at their current age: doing that would stretch the
                 y-axis to 23h for one stuck `pending` and squash the 8s–5m
                 band where the whole fleet actually lives. A tick says "this
                 was issued here and has not landed", which is the true claim.

     ISSUE → SETTLE  one mark per settled command at (issued_at, latency), on
                 a log y-axis because the sample spans 8s to 5m. p50 and p95
                 are drawn as rules with their values printed, so a mark is
                 read against a threshold rather than against a memory
                 (§13 tell 15).

   The window is fixed to the whole audit read, NOT to the filtered set, so
   narrowing to one robot leaves the axis where it was and the marks visibly
   thin out — the filter restructures the view without moving the ruler.

   Nothing here is derived beyond `updated_at − issued_at`, which is labelled
   as exactly that everywhere it appears.
   ========================================================================== */

/** Plot geometry. The band is the page's floor; it does not get to be tall —
    but it does have to be tall enough to hold the crosshair's own readout,
    which the panel body clips, and to give a log decade more than 16px. */
const PLOT_H = 62;
const PAD_T = 6;
const PAD_B = 6;
/** Half a mark, in px — marks are centred on their point. */
const MARK = 5;

/** Rows the crosshair readout draws before it defers to its own count. */
const HIT_CAP = 3;

const SEC = 1000;
const MIN = 60 * SEC;

/** Log-axis ticks an operator thinks in. */
const Y_TICKS: readonly { ms: number; label: string }[] = [
  { ms: 1 * SEC, label: '1s' },
  { ms: 5 * SEC, label: '5s' },
  { ms: 10 * SEC, label: '10s' },
  { ms: 30 * SEC, label: '30s' },
  { ms: 1 * MIN, label: '1m' },
  { ms: 2 * MIN, label: '2m' },
  { ms: 5 * MIN, label: '5m' },
  { ms: 10 * MIN, label: '10m' },
  { ms: 30 * MIN, label: '30m' },
  { ms: 60 * MIN, label: '1h' },
  { ms: 6 * 60 * MIN, label: '6h' },
  { ms: 24 * 60 * MIN, label: '24h' },
];

interface Mark {
  cmd: LedgerCommand;
  /** 0–1 across the window. */
  f: number;
  /** px from the top of the plot box. */
  y: number;
  ms: number;
}

export function FlowStrip({
  commands,
  from,
  now,
  clock,
  read,
  health,
  selectedId,
  onPick,
  connected,
  filtered,
  capped,
  className,
}: {
  /** The filtered row set — exactly what the board is showing. */
  commands: readonly LedgerCommand[];
  /** Window start: the oldest event in the audit read, filters ignored. */
  from: number;
  /**
   * The right-hand edge of the ruler: the board's clock, which STOPS at the
   * last good read when the feed dies. A time axis that keeps advancing past
   * the last datum draws a widening empty corridor and calls it "now".
   */
  now: number;
  /** The wall clock, for the freshness chip's own age. Always ticking. */
  clock: number;
  /**
   * THE READS THE MARKS CAME FROM — both of the board's endpoints. `0 settled
   * · 0 in flight` over a plot with nothing on it is the strip reporting a
   * quiet hour, and on a cold start or an outage nobody has looked.
   */
  read: Read | readonly Read[];
  /** This panel's own read health — never a constant. */
  health: Health;
  selectedId: number | null;
  onPick: (c: LedgerCommand) => void;
  connected: boolean;
  /** True when a filter is narrowing the set, for the header's own honesty. */
  filtered: boolean;
  /** True when the audit read hit its cap — the window's left edge is then a
      limit of the read, not the start of the history. */
  capped: boolean;
  className?: string | undefined;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  /* The marks are laid out in the PLOT's coordinate space, which is inset from
     the frame by the y-axis gutter. Measuring the pointer against the frame
     put the crosshair ~56px right of where the operator was pointing and made
     the hit test miss every mark it was drawn over — the readout below is only
     an instrument if the line, the cursor and the sample are the same x. */
  const plotRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState<{ f: number; t: number; x: number } | null>(null);

  const model = useMemo(() => {
    const settled: { cmd: LedgerCommand; ms: number }[] = [];
    const flying: LedgerCommand[] = [];
    for (const c of commands) {
      const ms = latencyMs(c);
      if (ms !== null && !IN_FLIGHT.has(c.status)) settled.push({ cmd: c, ms });
      else if (IN_FLIGHT.has(c.status)) flying.push(c);
      else if (ms !== null) settled.push({ cmd: c, ms });
    }
    const sample = settled.map((s) => s.ms).sort((a, b) => a - b);
    const p50 = percentile(sample, 0.5);
    const p95 = percentile(sample, 0.95);
    /* The domain is the sample's own range with a hair of headroom, NOT the
       next round number above it: snapping 5m up to 10m would spend half the
       band on latencies the fleet has never produced. The gridlines then land
       on whichever ladder values fall inside, so the labels stay readable
       without the axis being padded out to meet them. */
    const hi = (sample[sample.length - 1] ?? MIN) * 1.15;
    const lo = (sample[0] ?? SEC) * 0.85;
    const loT = Math.max(SEC / 2, Math.min(lo, hi / 4));
    const hiT = Math.max(hi, loT * 4);
    const a = Math.log10(loT);
    const b = Math.log10(hiT);
    const usable = PLOT_H - PAD_T - PAD_B;
    const yOf = (ms: number) =>
      PLOT_H - PAD_B - ((Math.log10(Math.min(Math.max(ms, loT), hiT)) - a) / (b - a)) * usable;

    const marks: Mark[] = settled.map(({ cmd, ms }) => ({
      cmd,
      ms,
      f: frac(Date.parse(cmd.issued_at), from, now),
      y: yOf(ms),
    }));

    /* Gridlines thinned to whatever fits: five labels in a 50px band is a
       stack of 10px type, not an axis. Walk the ladder from the top and keep
       a value only where it clears the last one it drew by 14px. */
    const grid: { ms: number; label: string; y: number }[] = [];
    for (const t of [...Y_TICKS].reverse()) {
      if (t.ms < loT || t.ms > hiT) continue;
      const y = yOf(t.ms);
      const prev = grid[grid.length - 1];
      if (prev !== undefined && y - prev.y < 14) continue;
      grid.push({ ms: t.ms, label: t.label, y });
    }

    return {
      marks,
      flying: flying.map((c) => ({ cmd: c, f: frac(Date.parse(c.issued_at), from, now) })),
      grid,
      p50,
      p95,
      y50: p50 === null ? null : yOf(p50),
      y95: p95 === null ? null : yOf(p95),
      n: settled.length,
    };
  }, [commands, from, now]);

  const axis = useMemo(() => ticks(from, now, 8), [from, now]);

  /* The hit list under the cursor: everything within ±7px of it, newest
     first, capped so the readout stays a readout and not a second table. The
     cap is three because the card has to fit inside the band it floats over —
     the count of everything in the sample is printed in its header, so a
     denser instant is stated rather than silently truncated. */
  const cursorSample = useMemo(() => {
    if (cursor === null) return [];
    const w = plotRef.current?.getBoundingClientRect().width ?? 0;
    if (w === 0) return [];
    const tol = 7 / w;
    return model.marks
      .filter((m) => Math.abs(m.f - cursor.f) <= tol)
      .concat(
        model.flying
          .filter((m) => Math.abs(m.f - cursor.f) <= tol)
          .map((m) => ({ cmd: m.cmd, f: m.f, y: 0, ms: -1 })),
      )
      .sort((x, y) => Date.parse(y.cmd.issued_at) - Date.parse(x.cmd.issued_at));
  }, [cursor, model]);

  const hits = useMemo(() => cursorSample.slice(0, HIT_CAP), [cursorSample]);

  const onMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const el = plotRef.current;
    if (el === null) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0) return;
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    /* The readout is anchored to the pointer, not parked in the middle of the
       band: a value block that does not travel with the playhead is a legend,
       and the operator has to carry the x across from one to the other. */
    const host = hostRef.current?.getBoundingClientRect();
    setCursor({ f, t: from + f * (now - from), x: host === undefined ? 0 : e.clientX - host.left });
  }, [from, now]);

  const commit = useCallback(() => {
    const first = hits[0];
    if (first !== undefined) onPick(first.cmd);
  }, [hits, onPick]);

  const span = duration(now - from);
  const flyN = model.flying.length;

  const summary =
    `Dispatch flow over ${span}: ${model.n} settled commands, median issue to settle ` +
    `${model.p50 === null ? 'no sample' : duration(model.p50)}, ` +
    `${flyN} still in flight.`;

  return (
    <Panel
      className={className === undefined ? styles.panel : `${styles.panel} ${className}`}
      title="Dispatch flow"
      meta={
        <>
          <span className={styles.meta}>
            <Figure of={read} value={model.n} /> settled
            <span className={styles.dim}> · </span>
            <Figure of={read} value={flyN} /> in flight
            {filtered && <span className={styles.dim}> · filtered</span>}
          </span>
          {/* The plot's own legend, in its header, keyed by the rule each
              value draws — so the two dashed lines on the band are named
              without a box of pills sitting over the data. */}
          <span className={styles.meta} title="Issue → settle, over every settled command drawn">
            <span className={styles.keyRule} data-kind="p50" aria-hidden="true" />
            p50 {model.p50 === null ? NO_VALUE : duration(model.p50)}
            <span className={styles.dim}> · </span>
            <span className={styles.keyRule} data-kind="p95" aria-hidden="true" />
            p95 {model.p95 === null ? NO_VALUE : duration(model.p95)}
          </span>
          {/* §13 tells 14 and 22 — an axis that does not say which quantity it
              carries, and a time axis that does not say which time. A command
              carries three timestamps (issued, updated, and the audit event's
              own ts) and this band uses two of them, so both are named in the
              contract's own field names rather than in a paraphrase.

              The window is named too, and when the audit read came back at its
              cap the ruler says so: the left edge is then the oldest event the
              platform would hand over, not the oldest event that exists, and a
              band that does not admit that is claiming coverage it has not
              got (§13 tell 21). */}
          <span
            className={styles.meta}
            title={
              'x = issued_at, y = updated_at − issued_at over every settled command drawn.' +
              (capped
                ? ' The audit read returned its cap, so this window starts at the oldest event it would hand over — there is older history off the left edge.'
                : '')
            }
          >
            x <span className={styles.dim}>issued_at</span>
            <span className={styles.dim}> · </span>
            y <span className={styles.dim}>updated−issued</span>
            <span className={styles.dim}> · </span>
            {span} window
            {capped && <span className={styles.capped}> capped</span>}
          </span>
          <FeedTag health={health} now={clock} pulse={connected} read={read} />
        </>
      }
      pad="none"
      scroll={false}
    >
      <div
        className={styles.frame}
        data-stale={!health.live}
        ref={hostRef}
        onPointerMove={onMove}
        onPointerLeave={() => setCursor(null)}
        onClick={commit}
        role="img"
        aria-label={summary}
      >
        {/* ---- in-flight lane: a tick where each unsettled command started -- */}
        <div className={styles.flyLane}>
          <span className={styles.laneLabel}>in flight</span>
          {model.flying.map((m) => (
            <span
              key={m.cmd.id}
              className={styles.flyTick}
              data-tone={CMD_TONE[m.cmd.status]}
              data-selected={m.cmd.id === selectedId}
              style={{ left: `${m.f * 100}%` }}
            />
          ))}
          {/* `nothing outstanding` is a measurement of the queue. On an
              unread source the lane says nothing at all: the plot 40px below
              carries the one line, and two identical `Reading…`s in one panel
              is the restatement this module keeps deleting. */}
          {flyN === 0 && !unread(read) && (
            <span className={styles.laneNone}>nothing outstanding</span>
          )}
        </div>

        {/* ---- the scatter ------------------------------------------------- */}
        <div className={styles.plot} ref={plotRef} style={{ height: `${PLOT_H}px` }}>
          {model.grid.map((g) => (
            <span key={g.ms} className={styles.hgrid} style={{ top: `${g.y}px` }}>
              <span className={styles.ygridLabel}>{g.label}</span>
            </span>
          ))}
          {axis.map((t) => (
            <span key={`v${t.t}`} className={styles.vgrid} style={{ left: `${t.f * 100}%` }} />
          ))}
          {model.y95 !== null && (
            <span className={styles.rule} data-kind="p95" style={{ top: `${model.y95}px` }} />
          )}
          {model.y50 !== null && (
            <span className={styles.rule} data-kind="p50" style={{ top: `${model.y50}px` }} />
          )}
          {model.marks.map((m) => (
            <span
              key={m.cmd.id}
              className={styles.mark}
              data-tone={CMD_TONE[m.cmd.status]}
              data-settled={m.cmd.status !== 'failed'}
              data-selected={m.cmd.id === selectedId}
              style={{
                left: `${m.f * 100}%`,
                top: `${m.y}px`,
                width: `${MARK}px`,
                height: `${MARK}px`,
              }}
            />
          ))}
          {model.n === 0 && (
            <span className={styles.plotNone}>
              {/* `Nothing has settled in this window.` is a measurement of the
                  window. Withheld where nobody read it. */}
              {unread(read) ? unreadLine(read) : 'Nothing has settled in this window.'}
            </span>
          )}
          {cursor !== null && (
            <span className={styles.cursor} style={{ left: `${cursor.f * 100}%` }} />
          )}
        </div>

        {/* ---- the ruler --------------------------------------------------- */}
        <div className={styles.axis}>
          {axis.map((t) =>
            t.f > 0.94 ? null : (
              <span key={t.t} className={styles.tick} style={{ left: `${t.f * 100}%` }}>
                {t.label}
              </span>
            ),
          )}
          <span className={styles.axisNow} data-stale={!health.live}>
            {health.live ? 'now' : 'last read'}
          </span>
        </div>

        {/* The crosshair readout. It answers at every x, not only over a mark:
            an instant with nothing in it is a real answer on a dispatch board
            — it is a gap in the fleet's traffic — and a tooltip that vanishes
            between marks teaches the operator that the plot is decoration.
            The instant is always stated; the sample under the cursor follows
            it when there is one. */}
        {cursor !== null && (
          <div
            className={styles.readout}
            style={{ left: `${cursor.x}px` }}
            data-side={cursor.f > 0.55 ? 'left' : 'right'}
          >
            <div className={styles.readoutTime}>
              {axisClock(cursor.t)}
              <span className={styles.readoutSpan}>
                {cursorSample.length === 0
                  ? 'no command within ±7px'
                  : `${cursorSample.length} within ±7px`}
              </span>
            </div>
            {hits.map((h) => (
              <div key={h.cmd.id} className={styles.readoutRow} data-tone={CMD_TONE[h.cmd.status]}>
                <span className={styles.rId}>#{h.cmd.id}</span>
                <span className={styles.rRobot}>{h.cmd.robot_id}</span>
                <span className={styles.rVerb}>{h.cmd.command}</span>
                <span className={styles.rState}>{h.cmd.status}</span>
                <span className={styles.rLat}>{h.ms < 0 ? NO_VALUE : duration(h.ms)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
